import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Bell, X } from "lucide-react";
import { updates } from "../data/updates";
import "./UpdateNotice.css";

export default function UpdateNotice({ open, onClose, onMute, onDetails }) {
  const backdrop = useRef(null);
  const closeButton = useRef(null);
  const muteTimer = useRef(null);
  const [muting, setMuting] = useState(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!open) return undefined;
    setMuting(false);
    const focused = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const siblings = Array.from(backdrop.current?.parentElement?.children || [])
      .filter((element) => element !== backdrop.current);
    const previous = siblings.map((element) => [element, element.inert]);
    siblings.forEach((element) => { element.inert = true; });
    closeButton.current?.focus({ preventScroll: true });
    return () => {
      window.clearTimeout(muteTimer.current);
      document.body.style.overflow = overflow;
      previous.forEach(([element, inert]) => { element.inert = inert; });
      if (focused?.isConnected) focused.focus?.({ preventScroll: true });
    };
  }, [open]);

  const handleKeyDown = (event) => {
    if (event.key === "Escape") { event.stopPropagation(); onClose(); }
    if (event.key !== "Tab") return;
    const buttons = Array.from(backdrop.current.querySelectorAll('button:not(:disabled), a[href], [tabindex="0"]'));
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };

  const mute = () => {
    if (muting) return;
    setMuting(true);
    muteTimer.current = window.setTimeout(onMute, reduceMotion ? 0 : 220);
  };

  return <AnimatePresence>
    {open && <motion.div ref={backdrop} className="update-notice-backdrop"
      role="dialog" aria-modal="true" aria-labelledby="update-notice-title" onKeyDown={handleKeyDown}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.25 }}>
      <motion.div className="update-notice-group"
        initial={reduceMotion ? false : { opacity: 0, y: 28, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }}
        transition={{ type: "spring", stiffness: 270, damping: 29, mass: 0.8 }}>
        <section className="update-notice-panel">
          <header className="update-notice-header">
            <span className="update-notice-symbol"><Bell size={19} /></span>
            <p>LUMANOVA / WHAT'S NEW</p>
            <h2 id="update-notice-title">最近，有这些新变化</h2>
          </header>
          <div className="update-notice-list" tabIndex={0} aria-label="最近更新记录">
            {updates.map((update) => <article className="update-notice-entry" key={update.id}>
              <div className="update-notice-date"><time dateTime={update.date}>{update.date}</time><span>{update.category}</span></div>
              <h3>{update.title}</h3>
              <ul>{(update.highlights || [update.summary]).map((line) => <li key={line}><span>{line}</span></li>)}</ul>
            </article>)}
          </div>
          <button type="button" className="update-notice-details" onClick={onDetails}>查看完整公告<ArrowUpRight size={16} /></button>
        </section>
        <div className="update-notice-controls">
          <button type="button" role="checkbox" aria-checked={muting} className="update-notice-mute"
            onClick={mute} title="本次登录有效期间不再提醒，重新登录后恢复">
            <span className={`update-notice-radio ${muting ? "is-checked" : ""}`} aria-hidden="true"><i /></span>
            本次登录不再提醒
          </button>
          <button ref={closeButton} type="button" className="update-notice-close" onClick={onClose}
            aria-label="关闭更新提醒" title="关闭更新提醒"><X size={20} /></button>
        </div>
      </motion.div>
    </motion.div>}
  </AnimatePresence>;
}
